import { Link } from 'react-router-dom';
import styled from 'styled-components';

import { Button } from '../primitives/Button';
import { Container } from '../primitives/Container';
import { Eyebrow } from '../primitives/Eyebrow';
import { StatBlock } from '../primitives/StatBlock';
import type { Section } from '../../content/schemas';

interface SectionRendererProps {
  section: Section;
}

const SectionWrap = styled.section`
  padding: ${(p) => p.theme.step[8]}px 0;
`;

const HeroHeadline = styled.h1`
  font-family: ${(p) => p.theme.font.serif};
  font-size: clamp(${(p) => p.theme.step[8]}px, 6vw, ${(p) => p.theme.step[10]}px);
  letter-spacing: -0.02em;
  line-height: 1.05;
  margin: 16px 0;

  em {
    font-style: italic;
    color: ${(p) => p.theme.color.accent.amber};
  }
`;

const HeroSub = styled.p`
  margin: 0;
  font-size: ${(p) => p.theme.step[5]}px;
  color: ${(p) => p.theme.color.text[1]};
  max-width: 640px;
  line-height: 1.5;
`;

const Ctas = styled.div`
  display: flex;
  gap: 12px;
  margin-top: ${(p) => p.theme.step[5]}px;
  flex-wrap: wrap;
`;

const HeroMeta = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: ${(p) => p.theme.step[5]}px;
  margin-top: ${(p) => p.theme.step[8]}px;
  border-top: 1px solid ${(p) => p.theme.color.border.faint};
  padding-top: ${(p) => p.theme.step[5]}px;
`;

const IntroGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1.4fr);
  gap: ${(p) => p.theme.step[7]}px;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const IntroHeading = styled.h2`
  font-family: ${(p) => p.theme.font.serif};
  font-size: clamp(${(p) => p.theme.step[6]}px, 4vw, ${(p) => p.theme.step[9]}px);
  margin: 12px 0 0;
  letter-spacing: -0.02em;
`;

const IntroBody = styled.p`
  font-size: ${(p) => p.theme.step[5]}px;
  color: ${(p) => p.theme.color.text[1]};
  line-height: 1.6;
  margin: 0;
`;

const ThreeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: ${(p) => p.theme.step[6]}px;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const ThreeColumnItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ThreeColumnNum = styled.span`
  font-family: ${(p) => p.theme.font.mono};
  font-size: ${(p) => p.theme.step[7]}px;
  color: ${(p) => p.theme.color.accent.amber};
`;

const ThreeColumnHeading = styled.h3`
  margin: 0;
  font-family: ${(p) => p.theme.font.serif};
  font-size: ${(p) => p.theme.step[6]}px;
  letter-spacing: -0.01em;
`;

const ThreeColumnBody = styled.p`
  margin: 0;
  font-size: ${(p) => p.theme.step[3]}px;
  color: ${(p) => p.theme.color.text[1]};
  line-height: 1.55;
`;

const Quote = styled.blockquote`
  margin: 0;
  padding: ${(p) => p.theme.step[7]}px 0;
  font-family: ${(p) => p.theme.font.serif};
  font-size: clamp(${(p) => p.theme.step[6]}px, 3.5vw, ${(p) => p.theme.step[9]}px);
  line-height: 1.25;
  letter-spacing: -0.01em;
  border-top: 1px solid ${(p) => p.theme.color.border.faint};
  border-bottom: 1px solid ${(p) => p.theme.color.border.faint};
  text-align: center;
  color: ${(p) => p.theme.color.text[0]};
`;

const Cite = styled.cite`
  display: block;
  margin-top: 16px;
  font-style: normal;
  font-family: ${(p) => p.theme.font.mono};
  font-size: ${(p) => p.theme.step[1]}px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${(p) => p.theme.color.text[3]};
`;

const CtaStripWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${(p) => p.theme.step[5]}px;
  align-items: center;
  justify-content: space-between;
  padding: ${(p) => p.theme.step[7]}px;
  border: 1px solid ${(p) => p.theme.color.border.med};
  border-radius: 12px;
  background: ${(p) => p.theme.color.bg[1]};
`;

const CtaStripHeading = styled.h2`
  margin: 0;
  font-family: ${(p) => p.theme.font.serif};
  font-size: ${(p) => p.theme.step[7]}px;
  letter-spacing: -0.01em;
`;

const CodeWrap = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1.2fr);
  gap: ${(p) => p.theme.step[6]}px;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const CodeBlock = styled.pre`
  margin: 0;
  padding: ${(p) => p.theme.step[5]}px;
  background: ${(p) => p.theme.color.bg[1]};
  border: 1px solid ${(p) => p.theme.color.border.soft};
  border-radius: 10px;
  font-family: ${(p) => p.theme.font.mono};
  font-size: ${(p) => p.theme.step[2]}px;
  color: ${(p) => p.theme.color.text[1]};
  overflow: auto;
  line-height: 1.6;
`;

function CtaButton({
  cta,
  variant = 'default',
}: {
  cta: { label: string; href: string };
  variant?: 'primary' | 'ghost' | 'default';
}) {
  const external =
    cta.href.startsWith('mailto:') ||
    cta.href.startsWith('tel:') ||
    /^https?:\/\//.test(cta.href);
  if (external) {
    return (
      <Button as="a" href={cta.href} $variant={variant}>
        {cta.label}
      </Button>
    );
  }
  return (
    <Button as={Link} to={cta.href} $variant={variant}>
      {cta.label}
    </Button>
  );
}

export function SectionRenderer({ section }: SectionRendererProps) {
  switch (section.kind) {
    case 'hero':
      return (
        <SectionWrap>
          <Container>
            {section.eyebrow && <Eyebrow $variant="amber">{section.eyebrow}</Eyebrow>}
            <HeroHeadline dangerouslySetInnerHTML={{ __html: section.headline }} />
            <HeroSub>{section.sub}</HeroSub>
            <Ctas>
              {section.ctas.map((c) => (
                <CtaButton key={c.href + c.label} cta={c} variant={c.variant} />
              ))}
            </Ctas>
            {section.meta && (
              <HeroMeta>
                {section.meta.map((m) => (
                  <StatBlock key={m.key} label={m.key} value={m.value} detail={m.detail} />
                ))}
              </HeroMeta>
            )}
          </Container>
        </SectionWrap>
      );
    case 'section-intro':
      return (
        <SectionWrap>
          <Container>
            <IntroGrid>
              <div>
                <Eyebrow $variant="amber">{section.eyebrow}</Eyebrow>
                <IntroHeading>{section.heading}</IntroHeading>
              </div>
              <IntroBody>{section.body}</IntroBody>
            </IntroGrid>
          </Container>
        </SectionWrap>
      );
    case 'three-column':
      return (
        <SectionWrap>
          <Container>
            <ThreeGrid>
              {section.columns.map((c) => (
                <ThreeColumnItem key={c.heading}>
                  <ThreeColumnNum>{c.num}</ThreeColumnNum>
                  <ThreeColumnHeading>{c.heading}</ThreeColumnHeading>
                  <ThreeColumnBody>{c.body}</ThreeColumnBody>
                </ThreeColumnItem>
              ))}
            </ThreeGrid>
          </Container>
        </SectionWrap>
      );
    case 'pull-quote':
      return (
        <SectionWrap>
          <Container>
            <Quote>
              “{section.quote}”
              <Cite>— {section.cite}</Cite>
            </Quote>
          </Container>
        </SectionWrap>
      );
    case 'cta-strip':
      return (
        <SectionWrap>
          <Container>
            <CtaStripWrap>
              <CtaStripHeading>{section.heading}</CtaStripHeading>
              <Ctas style={{ marginTop: 0 }}>
                {section.ctas.map((c) => (
                  <CtaButton
                    key={c.href + c.label}
                    cta={c}
                    variant={c.variant === 'primary' ? 'primary' : 'default'}
                  />
                ))}
              </Ctas>
            </CtaStripWrap>
          </Container>
        </SectionWrap>
      );
    case 'code-sample':
      return (
        <SectionWrap>
          <Container>
            <CodeWrap>
              <div>
                <Eyebrow $variant="amber">{section.language.toUpperCase()}</Eyebrow>
                <IntroHeading>{section.heading}</IntroHeading>
                <IntroBody style={{ marginTop: 16 }}>{section.body}</IntroBody>
              </div>
              <CodeBlock>
                <code>{section.code}</code>
              </CodeBlock>
            </CodeWrap>
          </Container>
        </SectionWrap>
      );
  }
}
